import { useIsMobile } from '@/shared/hooks/useMobile'
import { FormLabel } from '@mui/material'
import { TooltipProps } from '@radix-ui/react-tooltip'
import { useCallback, useMemo } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useLocalStorage, useSessionStorage } from 'usehooks-ts'
import { useUserContext } from '../contexts/userContext'
import { useToast } from '../hooks/useToast'

import { USER_PROFILE_AI_QUESTIONS_REQUIRED_SCHEMA } from '../schemas/account-profile'
import { Checkbox } from './ui/checkbox'
import { Tooltip, TooltipContent, TooltipTrigger } from './ui/tooltip'
import { Typography } from './ui/typography'

type ProfileUpdateReminderTooltipProps = TooltipProps & {
  hidden?: boolean
}

const LOCAL_STORAGE_KEY = 'ai-onboarding-dismissal'
const SESSION_STORAGE_KEY = 'ai-onboarding-temporally-dismissed'

export function ProfileUpdateReminderTooltip({
  children,
  ...props
}: ProfileUpdateReminderTooltipProps) {
  const { user, isLoading } = useUserContext()
  const location = useLocation()
  const isMobile = useIsMobile()

  const [isTemporallyDismissed, setTemporallyDismissed] = useSessionStorage(
    SESSION_STORAGE_KEY,
    false
  )
  const [dismissedUsers, setDismissedUsers] = useLocalStorage(
    LOCAL_STORAGE_KEY,
    []
  )

  const dismissed = dismissedUsers.includes(user?._id)

  const requiresProfileUpdate = useMemo(() => {
    return !USER_PROFILE_AI_QUESTIONS_REQUIRED_SCHEMA.safeParse(user?.profile)
      .success
  }, [user?.profile])

  const { toast } = useToast()

  const savePreferences = useCallback(
    (isDismissed: boolean) => {
      try {
        if (isDismissed) {
          setDismissedUsers((v) => Array.from(new Set(v).add(user._id)))
        } else if (!isDismissed && dismissedUsers.includes(user._id)) {
          setDismissedUsers((v) => v.filter((uId) => uId !== user._id))
        }
      } catch {
        toast({
          title: 'Failed to save preferences',
          description: 'Please try again',
          variant: 'destructive',
        })
      }
    },
    [dismissedUsers, user, setDismissedUsers, toast]
  )

  if (isLoading) {
    return null
  }

  const shouldDisplayTooltip =
    !isTemporallyDismissed &&
    dismissed === false &&
    requiresProfileUpdate &&
    !location.search.includes('settings=') // Settings modal being currently displayed

  return (
    <Tooltip {...props} open={shouldDisplayTooltip && !props.hidden}>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent
        side={isMobile ? 'top' : 'right'}
        sideOffset={isMobile ? 0 : 8}
      >
        <div className="max-w-56 pb-2 pt-6 text-paper">
          <Typography variant="body2" className="font-medium">
            AI Settings
          </Typography>
          <Typography variant="caption" className="mt-1">
            Customize your{' '}
            <Link
              to="?settings=aiSettings"
              className="font-semibold underline"
              onClick={() => setTemporallyDismissed(true)}
            >
              AI Settings
            </Link>{' '}
            to get playbooks perfectly suited to your needs.
          </Typography>

          <div className="mt-4 flex gap-1 leading-6">
            <Checkbox
              checked={dismissed}
              onCheckedChange={() => savePreferences(!dismissed)}
              disabled={isLoading || dismissed === null}
              variant="dark"
              id="checkboxDismissal"
            />

            <FormLabel
              htmlFor="checkboxDismissal"
              className="!text-xs !font-medium !leading-4 !text-inherit"
            >
              Don’t show this message again
            </FormLabel>
          </div>
        </div>
      </TooltipContent>
    </Tooltip>
  )
}
